import { useAuth, useTranslation } from '@/context/AuthContext';
import { useState, useEffect } from 'react';
import { getFamilies, updateFamily, inviteMember, updateUser, gdprExport, gdprDeleteAccount, subscribePush, unsubscribePush, getPushStatus, getPushPreferences, updatePushPreferences } from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Separator } from '@/components/ui/separator';
import { Switch } from '@/components/ui/switch';
import { UserPlus, Globe, Users, User, ShieldCheck, Bell, BellOff, AlertTriangle, FileCheck, ListTodo, Wallet, CalendarDays, Plane, Moon } from 'lucide-react';
import { toast } from 'sonner';

const DEFAULT_PREFS = {
  incidents: true,
  compliance: true,
  tasks: true,
  budget: false,
  daily_digest: false,
  trip_updates: true,
  dnd_enabled: false,
  dnd_start: "22:00",
  dnd_end: "08:00"
};

const NOTIFICATION_CATEGORIES = [
  { key: 'incidents', icon: AlertTriangle, label: 'Incidents critiques', desc: 'Retards, annulations, urgences', color: 'text-red-500' },
  { key: 'compliance', icon: FileCheck, label: 'Formalités', desc: 'Passeport, visa, documents urgents', color: 'text-amber-500' },
  { key: 'tasks', icon: ListTodo, label: 'Rappels de tâches', desc: 'Tâches assignées et échéances', color: 'text-blue-500' },
  { key: 'budget', icon: Wallet, label: 'Alertes budget', desc: 'Dépassement de budget', color: 'text-emerald-500' },
  { key: 'daily_digest', icon: CalendarDays, label: 'Résumé quotidien', desc: 'Activités du jour', color: 'text-purple-500' },
  { key: 'trip_updates', icon: Plane, label: 'Mises à jour voyage', desc: 'Modifications et nouveautés', color: 'text-cyan-500' }
];

export default function SettingsPage() {
  const { user, families, setFamilies, language, switchLanguage, setUser } = useAuth();
  const { t } = useTranslation();
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteForm, setInviteForm] = useState({ email: '', role: 'member' });
  const [familyName, setFamilyName] = useState('');
  const [pushEnabled, setPushEnabled] = useState(false);
  const [pushLoading, setPushLoading] = useState(false);
  const [notifPrefs, setNotifPrefs] = useState(DEFAULT_PREFS);
  const [prefsLoading, setPrefsLoading] = useState(false);

  const family = families[0];

  useEffect(() => {
    if (family) setFamilyName(family.name);
    checkPushStatus();
    loadNotificationPrefs();
  }, [family]);

  const checkPushStatus = async () => {
    try {
      const res = await getPushStatus();
      setPushEnabled(res.data.subscribed);
      if (res.data.preferences) {
        setNotifPrefs(res.data.preferences);
      }
    } catch (e) {
      console.error('Push status check failed');
    }
  };

  const loadNotificationPrefs = async () => {
    try {
      const res = await getPushPreferences();
      if (res.data.preferences) {
        setNotifPrefs(res.data.preferences);
      }
    } catch (e) {
      console.error('Load prefs failed');
    }
  };

  const handleTogglePush = async (enabled) => {
    setPushLoading(true);
    try {
      if (enabled) {
        const permission = await Notification.requestPermission();
        if (permission !== 'granted') {
          toast.error('Permission de notification refusée');
          setPushLoading(false);
          return;
        }
        const registration = await navigator.serviceWorker.ready;
        const subscription = await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array('BEl62iUYgUivxIkv69yViEuiBIa-Ib9-SkvMeAtA3LFgDzkrxZJjSgSnfckjBJuBkr3qBUYIHBQFLXYp5Nksh8U')
        });
        await subscribePush(subscription.toJSON(), family?.family_id);
        setPushEnabled(true);
        toast.success('Notifications push activées');
      } else {
        await unsubscribePush();
        setPushEnabled(false);
        toast.success('Notifications push désactivées');
      }
    } catch (e) {
      toast.error('Erreur: ' + (e.message || 'Impossible de configurer les notifications'));
    }
    setPushLoading(false);
  };

  const handleToggleCategory = async (key, value) => {
    const newPrefs = { ...notifPrefs, [key]: value };
    setNotifPrefs(newPrefs);
    setPrefsLoading(true);
    try {
      await updatePushPreferences(newPrefs);
      toast.success('Préférences mises à jour');
    } catch (e) {
      setNotifPrefs(notifPrefs); // Rollback
      toast.error('Erreur de sauvegarde');
    }
    setPrefsLoading(false);
  };

  function urlBase64ToUint8Array(base64String) {
    const padding = '='.repeat((4 - base64String.length % 4) % 4);
    const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);
    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
  }

  const handleSaveFamily = async () => {
    if (!family || !familyName) return;
    await updateFamily(family.family_id, { name: familyName, default_language: language });
    const res = await getFamilies();
    setFamilies(res.data);
    toast.success(t('common.save'));
  };

  const handleInvite = async () => {
    if (!family || !inviteForm.email) return;
    try {
      await inviteMember(family.family_id, inviteForm);
      toast.success(`Invited ${inviteForm.email}`);
      setInviteForm({ email: '', role: 'member' });
      setInviteOpen(false);
      const res = await getFamilies();
      setFamilies(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error');
    }
  };

  const handleLanguageChange = async (lang) => {
    switchLanguage(lang);
    try {
      const res = await updateUser({ language: lang });
      setUser(res.data);
    } catch {}
  };

  const roleColors = {
    owner: 'bg-[#3A86FF]/10 text-[#3A86FF]',
    parent: 'bg-[#3A86FF]/10 text-[#3A86FF]',
    member: 'bg-[#00CC99]/10 text-[#00CC99]',
    viewer: 'bg-gray-100 text-gray-600',
    colleague: 'bg-[#F59E0B]/10 text-[#F59E0B]',
    personal_assistant: 'bg-purple-100 text-purple-700',
  };

  return (
    <div className="max-w-3xl space-y-8 animate-fade-in" data-testid="settings-page">
      <h1 className="text-2xl md:text-3xl text-[#1E293B]" >
        {t('settings.title')}
      </h1>

      {/* Profile */}
      <Card className="border border-[#E2E8F0]/40 rounded-2xl">
        <CardContent className="p-6">
          <h2 className="text-lg font-medium text-[#1E293B] mb-4 flex items-center gap-2" >
            <User className="w-5 h-5 text-[#3A86FF]" /> {t('settings.profile')}
          </h2>
          <div className="flex items-center gap-4 mb-6">
            <Avatar className="w-14 h-14">
              <AvatarImage src={user?.picture} />
              <AvatarFallback className="bg-[#3A86FF] text-white">{user?.name?.[0]}</AvatarFallback>
            </Avatar>
            <div>
              <p className="font-medium text-[#1E293B]">{user?.name}</p>
              <p className="text-sm text-[#64748B]">{user?.email}</p>
            </div>
          </div>
          <Separator className="mb-4" />
          <div>
            <Label className="text-sm text-[#64748B] flex items-center gap-2">
              <Globe className="w-4 h-4" /> {t('settings.language')}
            </Label>
            <Select value={language} onValueChange={handleLanguageChange}>
              <SelectTrigger className="mt-1 h-11 w-48 rounded-lg border-[#E2E8F0]" data-testid="language-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="fr">Francais</SelectItem>
                <SelectItem value="en">English</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Family */}
      {family && (
        <Card className="border border-[#E2E8F0]/40 rounded-2xl">
          <CardContent className="p-6">
            <h2 className="text-lg font-medium text-[#1E293B] mb-4 flex items-center gap-2" >
              <Users className="w-5 h-5 text-[#3A86FF]" /> {t('settings.family')}
            </h2>
            <div className="space-y-4">
              <div>
                <Label className="text-sm text-[#64748B]">{t('settings.family')} name</Label>
                <div className="flex gap-2 mt-1">
                  <Input value={familyName} onChange={e => setFamilyName(e.target.value)} className="h-11 rounded-lg border-[#E2E8F0]" data-testid="family-name-input" />
                  <Button onClick={handleSaveFamily} className="rounded-full bg-[#3A86FF] hover:bg-[#3A86FF]/90 text-white px-6" data-testid="save-family-btn">
                    {t('common.save')}
                  </Button>
                </div>
              </div>

              <Separator />

              {/* Members */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-medium text-[#1E293B]">{t('settings.members')}</h3>
                  <Dialog open={inviteOpen} onOpenChange={setInviteOpen}>
                    <DialogTrigger asChild>
                      <Button variant="outline" size="sm" className="rounded-full text-xs" data-testid="invite-member-btn">
                        <UserPlus className="w-3.5 h-3.5 mr-1" /> {t('settings.inviteMember')}
                      </Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader><DialogTitle>{t('settings.inviteMember')}</DialogTitle></DialogHeader>
                      <div className="space-y-4 mt-4">
                        <div>
                          <Label className="text-sm">Email</Label>
                          <Input value={inviteForm.email} onChange={e => setInviteForm({...inviteForm, email: e.target.value})} className="mt-1 h-11" data-testid="invite-email" />
                        </div>
                        <div>
                          <Label className="text-sm">Role</Label>
                          <Select value={inviteForm.role} onValueChange={v => setInviteForm({...inviteForm, role: v})}>
                            <SelectTrigger className="mt-1 h-11"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="parent">Parent</SelectItem>
                              <SelectItem value="member">Member</SelectItem>
                              <SelectItem value="viewer">Viewer</SelectItem>
                              <SelectItem value="colleague">Colleague</SelectItem>
                              <SelectItem value="personal_assistant">Personal Assistant</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <Button onClick={handleInvite} className="w-full rounded-full bg-[#3A86FF] hover:bg-[#3A86FF]/90 text-white" data-testid="send-invite-btn">
                          {t('settings.inviteMember')}
                        </Button>
                      </div>
                    </DialogContent>
                  </Dialog>
                </div>
                <div className="space-y-2">
                  {(family.members || []).map(mem => (
                    <div key={mem.member_id} className="flex items-center gap-3 p-3 rounded-xl bg-[#F1F5F9]/50">
                      <Avatar className="w-8 h-8">
                        <AvatarImage src={mem.user?.picture} />
                        <AvatarFallback className="bg-[#3A86FF] text-white text-xs">
                          {mem.user?.name?.[0] || '?'}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-[#1E293B] truncate">{mem.user?.name || mem.user_id}</p>
                        <p className="text-xs text-[#64748B] truncate">{mem.user?.email}</p>
                      </div>
                      <Badge className={`text-[10px] rounded-full border-0 ${roleColors[mem.role] || 'bg-gray-100 text-gray-600'}`}>
                        {mem.role}
                      </Badge>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* GDPR / Data */}
      <Card className="border border-[#E2E8F0]/40 rounded-2xl">
        <CardContent className="p-6">
          <h2 className="text-lg font-bold text-[#1E293B] mb-4 flex items-center gap-2">
            <ShieldCheck className="w-5 h-5 text-[#00CC99]" /> RGPD & Donnees
          </h2>
          <div className="space-y-3">
            <div className="flex items-center justify-between p-3 rounded-xl bg-[#F8FAFC]">
              <div>
                <p className="text-sm font-medium text-[#1E293B]">Exporter mes donnees</p>
                <p className="text-[10px] text-[#94A3B8]">Telecharger toutes vos donnees au format JSON</p>
              </div>
              <Button variant="outline" size="sm" className="rounded-xl text-xs"
                onClick={async () => { const res = await gdprExport(); const blob = new Blob([JSON.stringify(res.data, null, 2)], {type:'application/json'}); const url = URL.createObjectURL(blob); const a = document.createElement('a'); a.href = url; a.download = 'travel-copilot-export.json'; a.click(); }}
                data-testid="gdpr-export-btn">
                Exporter
              </Button>
            </div>
            <div className="flex items-center justify-between p-3 rounded-xl bg-red-50/50">
              <div>
                <p className="text-sm font-medium text-red-700">Supprimer mon compte</p>
                <p className="text-[10px] text-red-400">Suppression definitive de toutes les donnees</p>
              </div>
              <Button variant="outline" size="sm" className="rounded-xl text-xs border-red-200 text-red-500 hover:bg-red-50"
                onClick={async () => { if (window.confirm('Supprimer definitivement toutes vos donnees ?')) { await gdprDeleteAccount(); window.location.href = '/'; } }}
                data-testid="gdpr-delete-btn">
                Supprimer
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Push Notifications */}
      <Card className="border border-[#E2E8F0]/40 rounded-2xl">
        <CardContent className="p-6">
          <h2 className="text-lg font-bold text-[#1E293B] mb-4 flex items-center gap-2">
            {pushEnabled ? <Bell className="w-5 h-5 text-[#3A86FF]" /> : <BellOff className="w-5 h-5 text-[#94A3B8]" />}
            Notifications Push
          </h2>
          <div className="space-y-4">
            {/* Master toggle */}
            <div className="flex items-center justify-between p-4 rounded-xl bg-gradient-to-r from-[#3A86FF]/5 to-[#00CC99]/5">
              <div className="flex-1">
                <p className="text-sm font-medium text-[#1E293B]">Activer les notifications</p>
                <p className="text-[11px] text-[#64748B] mt-1">
                  Recevez des alertes en temps réel sur votre appareil
                </p>
              </div>
              <Switch
                checked={pushEnabled}
                onCheckedChange={handleTogglePush}
                disabled={pushLoading || !('Notification' in window)}
                data-testid="push-toggle"
                className="ml-4"
              />
            </div>

            {!('Notification' in window) && (
              <p className="text-xs text-amber-600 bg-amber-50 p-3 rounded-lg">
                Votre navigateur ne supporte pas les notifications push
              </p>
            )}

            {/* Category preferences */}
            <div className="space-y-1">
              <p className="text-xs font-semibold text-[#64748B] uppercase tracking-wide px-1">
                Préférences par catégorie
              </p>
              <div className="space-y-2">
                {NOTIFICATION_CATEGORIES.map(({ key, icon: Icon, label, desc, color }) => (
                  <div 
                    key={key}
                    className={`flex items-center justify-between p-3 rounded-xl transition-all ${
                      notifPrefs[key] ? 'bg-[#F8FAFC]' : 'bg-[#F8FAFC]/50'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`p-2 rounded-lg ${notifPrefs[key] ? 'bg-white shadow-sm' : 'bg-gray-100'}`}>
                        <Icon className={`w-4 h-4 ${notifPrefs[key] ? color : 'text-gray-400'}`} />
                      </div>
                      <div>
                        <p className={`text-sm font-medium ${notifPrefs[key] ? 'text-[#1E293B]' : 'text-gray-400'}`}>
                          {label}
                        </p>
                        <p className="text-[10px] text-[#94A3B8]">{desc}</p>
                      </div>
                    </div>
                    <Switch
                      checked={notifPrefs[key]}
                      onCheckedChange={(v) => handleToggleCategory(key, v)}
                      disabled={prefsLoading}
                      data-testid={`notif-${key}-toggle`}
                      className="scale-90"
                    />
                  </div>
                ))}
              </div>
            </div>

            {/* Do Not Disturb */}
            <div className="space-y-2">
              <p className="text-xs font-semibold text-[#64748B] uppercase tracking-wide px-1">
                Mode silence
              </p>
              <div className={`p-4 rounded-xl transition-all ${notifPrefs.dnd_enabled ? 'bg-indigo-50/50 border border-indigo-200' : 'bg-[#F8FAFC]'}`}>
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-lg ${notifPrefs.dnd_enabled ? 'bg-indigo-100' : 'bg-gray-100'}`}>
                      <Moon className={`w-4 h-4 ${notifPrefs.dnd_enabled ? 'text-indigo-500' : 'text-gray-400'}`} />
                    </div>
                    <div>
                      <p className={`text-sm font-medium ${notifPrefs.dnd_enabled ? 'text-indigo-700' : 'text-[#1E293B]'}`}>
                        Ne pas déranger
                      </p>
                      <p className="text-[10px] text-[#94A3B8]">Suspendre les notifications pendant vos heures de repos</p>
                    </div>
                  </div>
                  <Switch
                    checked={notifPrefs.dnd_enabled}
                    onCheckedChange={(v) => handleToggleCategory('dnd_enabled', v)}
                    disabled={prefsLoading}
                    data-testid="dnd-toggle"
                  />
                </div>
                {notifPrefs.dnd_enabled && (
                  <div className="flex items-center gap-3 mt-3 pt-3 border-t border-indigo-200/50">
                    <div className="flex-1">
                      <label className="text-[10px] text-indigo-600 font-medium">De</label>
                      <Input
                        type="time"
                        value={notifPrefs.dnd_start}
                        onChange={(e) => {
                          const newPrefs = { ...notifPrefs, dnd_start: e.target.value };
                          setNotifPrefs(newPrefs);
                          updatePushPreferences(newPrefs);
                        }}
                        className="h-9 text-sm mt-1"
                        data-testid="dnd-start"
                      />
                    </div>
                    <div className="flex-1">
                      <label className="text-[10px] text-indigo-600 font-medium">À</label>
                      <Input
                        type="time"
                        value={notifPrefs.dnd_end}
                        onChange={(e) => {
                          const newPrefs = { ...notifPrefs, dnd_end: e.target.value };
                          setNotifPrefs(newPrefs);
                          updatePushPreferences(newPrefs);
                        }}
                        className="h-9 text-sm mt-1"
                        data-testid="dnd-end"
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Active count */}
            <div className="flex items-center justify-between px-1">
              <p className="text-[11px] text-[#64748B]">
                {Object.entries(notifPrefs).filter(([k, v]) => !k.startsWith('dnd') && v === true).length} catégorie(s) active(s)
              </p>
              <Button 
                variant="ghost" 
                size="sm" 
                className="text-[10px] h-6 text-[#3A86FF]"
                onClick={() => {
                  const allOn = { ...notifPrefs, incidents: true, compliance: true, tasks: true, budget: true, daily_digest: true, trip_updates: true };
                  setNotifPrefs(allOn);
                  updatePushPreferences(allOn);
                  toast.success('Toutes les catégories activées');
                }}
              >
                Tout activer
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
